import { useState, useCallback, useRef } from 'react';

export function useInteraction() {
  // Single selection (legacy, für Kompatibilität)
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);

  // Multi-selection
  const [selectedPointIds, setSelectedPointIds] = useState([]);
  const [selectedWallIds, setSelectedWallIds] = useState([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);
  const [selectedShaftIds, setSelectedShaftIds] = useState([]);
  const [selectedStubWallIds, setSelectedStubWallIds] = useState([]);

  // Geometry selection (pure CAD objects)
  const [selectedGeometryPointIds, setSelectedGeometryPointIds] = useState([]);
  const [selectedGeometryCurveIds, setSelectedGeometryCurveIds] = useState([]);
  const [selectedGeometrySolidIds, setSelectedGeometrySolidIds] = useState([]);

  // Box/Rectangle Selection State
  const [selectionBox, setSelectionBox] = useState(null); // { start: {x,y}, end: {x,y}, mode: 'window'|'crossing' }

  // Opening selection (wallId + openingId)
  const [selectedOpeningWallId, setSelectedOpeningWallId] = useState(null);
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);

  // Shaft selection
  const [selectedShaftId, setSelectedShaftId] = useState(null);

  // StubWall selection
  const [selectedStubWallId, setSelectedStubWallId] = useState(null);

  // Hover State - EINZELNER State statt vieler separater States
  // Dies verhindert multiple setState Aufrufe die React Error #300 verursachen können
  const [hoverState, setHoverState] = useState({ type: null, id: null });
  const [isDragging, setIsDragging] = useState(false);

  // Abgeleitete Werte für Kompatibilität
  const hoveredPointId = hoverState.type === 'point' ? hoverState.id : null;
  const hoveredWallId = hoverState.type === 'wall' ? hoverState.id : null;
  const hoveredOpeningId = hoverState.type === 'opening' ? hoverState.id : null;
  const hoveredShaftId = hoverState.type === 'shaft' ? hoverState.id : null;
  const hoveredStubWallId = hoverState.type === 'stubWall' ? hoverState.id : null;
  const hoveredGeometryPointId = hoverState.type === 'geometryPoint' ? hoverState.id : null;
  const hoveredGeometryCurveId = hoverState.type === 'geometryCurve' ? hoverState.id : null;
  const hoveredGeometrySolidId = hoverState.type === 'geometrySolid' ? hoverState.id : null;

  // Hover Refs - für sofortige Updates ohne React Error #300
  // onPointerOver wird während des R3F Render-Loops aufgerufen
  // Daher NUR Ref updaten, State wird separat synchronisiert
  const hoverRef = useRef({ type: null, id: null });
  const lastSyncedHoverRef = useRef({ type: null, id: null });
  const hoverSyncScheduledRef = useRef(false);

  // Hover State synchronisieren - NUR wenn sich tatsächlich etwas geändert hat
  // Verwendet setTimeout statt RAF, da RAF während R3F Render laufen kann
  const scheduleHoverSync = useCallback(() => {
    if (hoverSyncScheduledRef.current) return;

    const current = hoverRef.current;
    const last = lastSyncedHoverRef.current;

    // Nur syncen wenn sich was geändert hat
    if (current.type === last.type && current.id === last.id) {
      return;
    }

    hoverSyncScheduledRef.current = true;

    // setTimeout mit 16ms (1 Frame) statt 0 für stabileres Verhalten
    setTimeout(() => {
      hoverSyncScheduledRef.current = false;

      const { type, id } = hoverRef.current;

      // Nochmal prüfen ob sich wirklich was geändert hat
      if (type === lastSyncedHoverRef.current.type && id === lastSyncedHoverRef.current.id) {
        return;
      }

      lastSyncedHoverRef.current = { type, id };

      // EIN setState-Aufruf statt 5
      setHoverState({ type, id });
    }, 16);
  }, []);

  // Punkt auswählen (mit Multi-Selection Support)
  const selectPoint = useCallback((pointId, addToSelection = false) => {
    if (addToSelection) {
      // Toggle in multi-selection
      setSelectedPointIds(prev =>
        prev.includes(pointId)
          ? prev.filter(id => id !== pointId)
          : [...prev, pointId]
      );
      // Auch single selection setzen (für Kompatibilität)
      setSelectedPointId(pointId);
    } else {
      // Single selection - ALLE anderen Selektionen zurücksetzen
      setSelectedPointId(pointId);
      setSelectedPointIds([pointId]);
      setSelectedWallId(null);
      setSelectedWallIds([]);
      setSelectedSpaceId(null);
      setSelectedSpaceIds([]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
    }
  }, []);

  // Wand auswählen (mit Multi-Selection Support)
  const selectWall = useCallback((wallId, addToSelection = false) => {
    if (addToSelection) {
      // Toggle in multi-selection
      setSelectedWallIds(prev =>
        prev.includes(wallId)
          ? prev.filter(id => id !== wallId)
          : [...prev, wallId]
      );
      // Auch single selection setzen (für Kompatibilität)
      setSelectedWallId(wallId);
    } else {
      // Single selection - ALLE anderen Selektionen zurücksetzen
      setSelectedPointId(null);
      setSelectedPointIds([]);
      setSelectedWallId(wallId);
      setSelectedWallIds([wallId]);
      setSelectedSpaceId(null);
      setSelectedSpaceIds([]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
    }
  }, []);

  // Raum auswählen (mit Multi-Selection Support)
  const selectSpace = useCallback((spaceId, addToSelection = false) => {
    if (addToSelection) {
      // Toggle in multi-selection
      setSelectedSpaceIds(prev =>
        prev.includes(spaceId)
          ? prev.filter(id => id !== spaceId)
          : [...prev, spaceId]
      );
      // Auch single selection setzen (für Kompatibilität)
      setSelectedSpaceId(spaceId);
    } else {
      // Single selection - ALLE anderen Selektionen zurücksetzen
      setSelectedPointId(null);
      setSelectedPointIds([]);
      setSelectedWallId(null);
      setSelectedWallIds([]);
      setSelectedSpaceId(spaceId);
      setSelectedSpaceIds([spaceId]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
    }
  }, []);

  // Öffnung auswählen
  const selectOpening = useCallback((wallId, openingId) => {
    // ALLE anderen Selektionen zurücksetzen
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(null);
    setSelectedWallIds([]);
    setSelectedSpaceId(null);
    setSelectedSpaceIds([]);
    setSelectedShaftId(null);
    setSelectedShaftIds([]);
    setSelectedStubWallId(null);
    setSelectedStubWallIds([]);
    // Set opening selection
    setSelectedOpeningWallId(wallId);
    setSelectedOpeningId(openingId);
  }, []);

  // Schacht auswählen
  const selectShaft = useCallback((shaftId) => {
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(null);
    setSelectedWallIds([]);
    setSelectedSpaceId(null);
    setSelectedSpaceIds([]);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);
    setSelectedStubWallId(null);
    setSelectedStubWallIds([]);
    setSelectedShaftId(shaftId);
    setSelectedShaftIds([shaftId]);
  }, []);

  // Vorwand auswählen
  const selectStubWall = useCallback((stubWallId) => {
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(null);
    setSelectedWallIds([]);
    setSelectedSpaceId(null);
    setSelectedSpaceIds([]);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);
    setSelectedShaftId(null);
    setSelectedShaftIds([]);
    setSelectedStubWallId(stubWallId);
    setSelectedStubWallIds([stubWallId]);
    // Clear geometry selection
    setSelectedGeometryPointIds([]);
    setSelectedGeometryCurveIds([]);
    setSelectedGeometrySolidIds([]);
  }, []);

  // Geometry Punkt auswählen
  const selectGeometryPoint = useCallback((pointId, addToSelection = false) => {
    if (addToSelection) {
      setSelectedGeometryPointIds(prev =>
        prev.includes(pointId)
          ? prev.filter(id => id !== pointId)
          : [...prev, pointId]
      );
    } else {
      // Clear all other selections
      setSelectedPointId(null);
      setSelectedPointIds([]);
      setSelectedWallId(null);
      setSelectedWallIds([]);
      setSelectedSpaceId(null);
      setSelectedSpaceIds([]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
      // Set geometry point selection
      setSelectedGeometryPointIds([pointId]);
      setSelectedGeometryCurveIds([]);
      setSelectedGeometrySolidIds([]);
    }
  }, []);

  // Geometry Kurve auswählen
  const selectGeometryCurve = useCallback((curveId, addToSelection = false) => {
    if (addToSelection) {
      setSelectedGeometryCurveIds(prev =>
        prev.includes(curveId)
          ? prev.filter(id => id !== curveId)
          : [...prev, curveId]
      );
    } else {
      // Clear all other selections
      setSelectedPointId(null);
      setSelectedPointIds([]);
      setSelectedWallId(null);
      setSelectedWallIds([]);
      setSelectedSpaceId(null);
      setSelectedSpaceIds([]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
      // Set geometry curve selection
      setSelectedGeometryPointIds([]);
      setSelectedGeometryCurveIds([curveId]);
      setSelectedGeometrySolidIds([]);
    }
  }, []);

  // Geometry Solid auswählen
  const selectGeometrySolid = useCallback((solidId, addToSelection = false) => {
    if (addToSelection) {
      setSelectedGeometrySolidIds(prev =>
        prev.includes(solidId)
          ? prev.filter(id => id !== solidId)
          : [...prev, solidId]
      );
    } else {
      // Clear all other selections
      setSelectedPointId(null);
      setSelectedPointIds([]);
      setSelectedWallId(null);
      setSelectedWallIds([]);
      setSelectedSpaceId(null);
      setSelectedSpaceIds([]);
      setSelectedOpeningWallId(null);
      setSelectedOpeningId(null);
      setSelectedShaftId(null);
      setSelectedShaftIds([]);
      setSelectedStubWallId(null);
      setSelectedStubWallIds([]);
      // Set geometry solid selection
      setSelectedGeometryPointIds([]);
      setSelectedGeometryCurveIds([]);
      setSelectedGeometrySolidIds([solidId]);
    }
  }, []);

  // Alles abwählen
  const clearSelection = useCallback(() => {
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(null);
    setSelectedWallIds([]);
    setSelectedSpaceId(null);
    setSelectedSpaceIds([]);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);
    setSelectedShaftId(null);
    setSelectedShaftIds([]);
    setSelectedStubWallId(null);
    setSelectedStubWallIds([]);
    // Clear geometry selection
    setSelectedGeometryPointIds([]);
    setSelectedGeometryCurveIds([]);
    setSelectedGeometrySolidIds([]);
  }, []);

  // Hover setzen - verwendet deferred sync um React Render-Conflicts zu vermeiden
  const setHover = useCallback((type, id) => {
    hoverRef.current = { type, id };
    scheduleHoverSync();
  }, [scheduleHoverSync]);

  // Hover zurücksetzen
  const clearHover = useCallback(() => {
    hoverRef.current = { type: null, id: null };
    scheduleHoverSync();
  }, [scheduleHoverSync]);

  // Alle Wände auswählen
  const selectAllWalls = useCallback((wallIds) => {
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(wallIds[0] || null);
    setSelectedWallIds(wallIds);
    setSelectedSpaceId(null);
    setSelectedSpaceIds([]);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);
    setSelectedShaftId(null);
    setSelectedShaftIds([]);
    setSelectedStubWallId(null);
    setSelectedStubWallIds([]);
  }, []);

  // Alle Räume auswählen
  const selectAllSpaces = useCallback((spaceIds) => {
    setSelectedPointId(null);
    setSelectedPointIds([]);
    setSelectedWallId(null);
    setSelectedWallIds([]);
    setSelectedSpaceId(spaceIds[0] || null);
    setSelectedSpaceIds(spaceIds);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);
    setSelectedShaftId(null);
    setSelectedShaftIds([]);
    setSelectedStubWallId(null);
    setSelectedStubWallIds([]);
  }, []);

  // Box Selection starten
  const startBoxSelection = useCallback((x, y) => {
    setSelectionBox({ start: { x, y }, end: { x, y }, mode: null });
  }, []);

  // Box Selection aktualisieren
  const updateBoxSelection = useCallback((x, y) => {
    setSelectionBox(prev => {
      if (!prev) return null;
      // Mode basiert auf Ziehrichtung: rechts->links = crossing, links->rechts = window
      const mode = x < prev.start.x ? 'crossing' : 'window';
      return { ...prev, end: { x, y }, mode };
    });
  }, []);

  // Box Selection beenden und Objekte auswählen
  const finishBoxSelection = useCallback((points, walls, spaces, shafts = [], stubWalls = []) => {
    if (!selectionBox) return;

    const { start, end, mode } = selectionBox;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    // Hilfsfunktion: Punkt im Rechteck?
    const pointInBox = (px, py) => px >= minX && px <= maxX && py >= minY && py <= maxY;

    // Hilfsfunktion: Linie schneidet Rechteck? (für Crossing)
    const lineIntersectsBox = (x1, y1, x2, y2) => {
      // Entweder ein Endpunkt ist im Rechteck
      if (pointInBox(x1, y1) || pointInBox(x2, y2)) return true;
      // Oder die Linie schneidet eine Rechteck-Kante
      const edges = [
        [minX, minY, maxX, minY], // bottom
        [maxX, minY, maxX, maxY], // right
        [maxX, maxY, minX, maxY], // top
        [minX, maxY, minX, minY], // left
      ];
      for (const [ex1, ey1, ex2, ey2] of edges) {
        if (linesIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2)) return true;
      }
      return false;
    };

    // Linien-Schnitttest
    const linesIntersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
      const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      if (Math.abs(denom) < 0.001) return false;
      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };

    // Hilfsfunktion: Polygon im/schneidet Rechteck?
    const polygonInOrIntersectsBox = (polygon, requireFullyInside) => {
      if (!polygon || polygon.length < 3) return false;
      const allInside = polygon.every(p => pointInBox(p.x, p.y));
      if (requireFullyInside) return allInside;
      if (allInside) return true;
      // Check edges
      for (let i = 0; i < polygon.length; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % polygon.length];
        if (lineIntersectsBox(p1.x, p1.y, p2.x, p2.y)) return true;
      }
      return false;
    };

    const selectedPts = [];
    const selectedWls = [];
    const selectedSps = [];
    const selectedShfts = [];
    const selectedStubs = [];

    // Punkte prüfen
    for (const point of points) {
      if (pointInBox(point.x, point.y)) {
        selectedPts.push(point.id);
      }
    }

    // Wände prüfen
    for (const wall of walls) {
      const startPt = points.find(p => p.id === wall.startPointId);
      const endPt = points.find(p => p.id === wall.endPointId);
      if (!startPt || !endPt) continue;

      if (mode === 'window') {
        // Beide Endpunkte müssen im Rechteck sein
        if (pointInBox(startPt.x, startPt.y) && pointInBox(endPt.x, endPt.y)) {
          selectedWls.push(wall.id);
        }
      } else {
        // Crossing: Linie muss Rechteck berühren
        if (lineIntersectsBox(startPt.x, startPt.y, endPt.x, endPt.y)) {
          selectedWls.push(wall.id);
        }
      }
    }

    // Räume prüfen (Polygone)
    for (const space of spaces) {
      const polygon = space.pointIds?.map(pid => points.find(p => p.id === pid)).filter(Boolean);
      if (polygonInOrIntersectsBox(polygon, mode === 'window')) {
        selectedSps.push(space.id);
      }
    }

    // Schächte prüfen
    for (const shaft of shafts) {
      const polygon = shaft.pointIds?.map(pid => points.find(p => p.id === pid)).filter(Boolean);
      if (polygonInOrIntersectsBox(polygon, mode === 'window')) {
        selectedShfts.push(shaft.id);
      }
    }

    // Vorwände prüfen
    for (const stubWall of stubWalls) {
      const polygon = stubWall.pointIds?.map(pid => points.find(p => p.id === pid)).filter(Boolean);
      if (polygonInOrIntersectsBox(polygon, mode === 'window')) {
        selectedStubs.push(stubWall.id);
      }
    }

    // Selektion setzen (inkl. Öffnungen zurücksetzen)
    setSelectedPointIds(selectedPts);
    setSelectedPointId(selectedPts[0] || null);
    setSelectedWallIds(selectedWls);
    setSelectedWallId(selectedWls[0] || null);
    setSelectedSpaceIds(selectedSps);
    setSelectedSpaceId(selectedSps[0] || null);
    setSelectedShaftIds(selectedShfts);
    setSelectedShaftId(selectedShfts[0] || null);
    setSelectedStubWallIds(selectedStubs);
    setSelectedStubWallId(selectedStubs[0] || null);
    setSelectedOpeningWallId(null);
    setSelectedOpeningId(null);

    // Box zurücksetzen
    setSelectionBox(null);
  }, [selectionBox]);

  // Box Selection abbrechen
  const cancelBoxSelection = useCallback(() => {
    setSelectionBox(null);
  }, []);

  return {
    // Single Selection (Kompatibilität)
    selectedPointId,
    selectedWallId,
    selectedSpaceId,
    selectPoint,
    selectWall,
    selectSpace,
    clearSelection,

    // Multi-Selection
    selectedPointIds,
    selectedWallIds,
    selectedSpaceIds,
    selectedShaftIds,
    selectedStubWallIds,
    selectAllWalls,
    selectAllSpaces,

    // Box Selection (Crossing/Window)
    selectionBox,
    startBoxSelection,
    updateBoxSelection,
    finishBoxSelection,
    cancelBoxSelection,

    // Opening Selection
    selectedOpeningWallId,
    selectedOpeningId,
    selectOpening,

    // Shaft Selection
    selectedShaftId,
    selectShaft,

    // StubWall Selection
    selectedStubWallId,
    selectStubWall,

    // Geometry Selection (pure CAD)
    selectedGeometryPointIds,
    selectedGeometryCurveIds,
    selectedGeometrySolidIds,
    selectGeometryPoint,
    selectGeometryCurve,
    selectGeometrySolid,
    setSelectedGeometryPointIds,

    // Hover
    hoveredPointId,
    hoveredWallId,
    hoveredOpeningId,
    hoveredShaftId,
    hoveredStubWallId,
    hoveredGeometryPointId,
    hoveredGeometryCurveId,
    hoveredGeometrySolidId,
    setHover,
    clearHover,

    // Drag
    isDragging,
    setIsDragging,
  };
}
